// Copyright 2026 Hardfin, Inc.
// Hardfin Confidential - Restricted
// API module for uploading company data via CSV to the Hardfin system.
// Provides type-safe upload with Zod validation for the import response.
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { sparseCustomerSchema } from '@/sparse/api/types';
import { z } from 'zod';

const validationErrorFieldSchema = z.object({
  fieldName: z.string(),
  error: z.string(),
  errorCode: z.number(),
});

const processedCompanyDataSchema = z.object({
  companyName: z.string(),
  index: z.number(),
  created: z.boolean(),
  updated: z.boolean(),
  noOp: z.boolean(),
  validatedCompany: sparseCustomerSchema.nullable(),
  fieldsUpdated: z.record(z.boolean()),
  errors: z.array(validationErrorFieldSchema),
});

const rowErrorDataSchema = z.object({
  index: z.number(),
  errors: z.array(validationErrorFieldSchema),
});

export const uploadCompanyDataResponseSchema = z.object({
  data: z.object({
    success: z.boolean(),
    items: z.array(processedCompanyDataSchema),
    structureErrors: z.array(validationErrorFieldSchema),
    rowErrors: z.array(rowErrorDataSchema),
  }),
});

export type CompanyDataUploadResult = z.infer<
  typeof uploadCompanyDataResponseSchema
>['data'];

/**
 * Uploads a CSV file containing company data for import.
 *
 * @param formData - FormData containing the CSV file under the "data" key
 * @returns Promise resolving to the upload result with processed companies and any errors
 * @throws Will re-throw any parsing error from the response validation
 */
export async function uploadCompanyData(
  formData: FormData
): Promise<CompanyDataUploadResult> {
  const url = '/v1/import/company-data';
  const response = await api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  const parsed = await parseResponse(
    uploadCompanyDataResponseSchema,
    response.data,
    {
      schemaName: 'UploadCompanyData',
      endpoint: url,
    }
  );
  return parsed.data;
}
